/**
 * Entity Cache Service
 *
 * Thin utility module retained for two purposes after the entity-cache IDB
 * layer was superseded by the entity-system Y.Doc (entitySystemYjsStore):
 *
 *   1. computeEntitiesHash() — pure hash used by entityStore staleness checks.
 *   2. delete() / clear()    — called during logout and dataspace switching to
 *                              evict any dataspace-keyed entries that remain
 *                              from older builds.
 *
 * The underlying IDB database (river_entity_cache) is deleted by migration
 * m001 in idbMigrations.ts, so these methods are now no-ops in practice for
 * new users. They are kept as no-ops so callers don't need to be updated yet.
 */

import { createFileLogger } from '../utils/logger';

const log = createFileLogger('ENTITY_CACHE');

class EntityCacheService {
  /**
   * Compute a deterministic hash from entity timestamps for staleness detection.
   * Pure utility — no IndexedDB access.
   */
  computeEntitiesHash(timestamps: Record<string, string>): string {
    const sortedEntries = Object.entries(timestamps)
      .sort(([a], [b]) => a.localeCompare(b));

    const hashInput = sortedEntries
      .map(([llmId, updatedAt]) => `${llmId}:${updatedAt}`)
      .join('|');

    return this.simpleStringHash(hashInput);
  }

  /** 32-bit FNV-1a */
  private simpleStringHash(str: string): string {
    let hash = 2166136261;
    for (let i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return (hash >>> 0).toString(36);
  }

  /**
   * Delete a specific cache key. No-op if the underlying DB no longer exists
   * (removed by migration m001).
   */
  async delete(_key: string): Promise<void> {
    log.debug('[EntityCache] delete() called (no-op — cache removed by migration m001)');
  }

  /**
   * Clear all cache entries. No-op if the underlying DB no longer exists.
   */
  async clear(): Promise<void> {
    log.debug('[EntityCache] clear() called (no-op — cache removed by migration m001)');
  }
}

export const entityCacheService = new EntityCacheService();
